import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Lock, Eye, FileCheck } from 'lucide-react';

const principles = [
  {
    icon: Lock,
    title: 'Absolute Confidentiality',
    description:
      'All consultations and matters are handled with the highest level of discretion and confidentiality, protected by attorney-client privilege.',
  },
  {
    icon: Eye,
    title: 'Private Advisory Approach',
    description:
      'Personal, direct counsel without the bureaucracy of large firms. Your matters receive undivided attention in a confidential setting.',
  },
  {
    icon: FileCheck,
    title: 'Ethical Excellence',
    description:
      'Uncompromising professional ethics and adherence to the highest standards of legal practice across all jurisdictions.',
  },
];

const Discretion = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-[#0f1419] to-[#1a2332] overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#c9a961]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#c9a961]/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <span className="text-sm text-[#c9a961] uppercase tracking-wider mb-4 block">Our Commitment</span>
          <h2 className="font-serif text-4xl md:text-6xl text-white mb-6 leading-tight">
            Discretion, Ethics & Privacy
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
            A confidential consulting room for sophisticated international clients
          </p>
        </motion.div>

        {/* Principles grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {principles.map((principle, index) => {
            const Icon = principle.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2 + index * 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group text-center"
              >
                {/* Icon */}
                <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6 mx-auto">
                  <div className="absolute inset-0 bg-[#c9a961]/10 rounded-full group-hover:scale-110 transition-transform duration-500" />
                  <Icon className="relative w-10 h-10 text-[#c9a961] group-hover:scale-110 transition-transform duration-500" />
                </div>

                {/* Title */}
                <h3 className="font-serif text-2xl text-white mb-4 group-hover:text-[#c9a961] transition-colors duration-500">
                  {principle.title}
                </h3>

                {/* Description */}
                <p className="text-gray-400 leading-relaxed">
                  {principle.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20 text-center"
        >
          <div className="max-w-4xl mx-auto p-8 md:p-12 border-l-2 border-[#c9a961]/50 bg-[#1a2332]/30">
            <p className="font-serif text-xl md:text-2xl text-gray-300 italic leading-relaxed mb-4">
              "A private consulting room, not a general law firm. Your matters are handled with the discretion
              and attention they deserve, protected by the highest standards of professional privilege and
              unwavering integrity proven and audited over decades."
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Discretion;
